import { reorderVisualizerStepsRuntime } from '@/lib/pipelineUtils';
import { DEFAULT_PANEL_CONFIG } from '@/lib/storeConstants';
import { useEditorStore } from '@/store/useEditorStore';
import { useExecutionStore } from '@/store/useExecutionStore';
import { useTabStore } from '@/store/useTabStore';
import { useVisualizerStore, type VisualizerMode } from '@/store/useVisualizerStore';

/** Save current appStore state into a specific tab. */
export function saveAppStateToTab(tabId: string) {
  const editor = useEditorStore.getState();
  const exec = useExecutionStore.getState();
  const viz = useVisualizerStore.getState();
  const { tabs } = useTabStore.getState();
  if (!tabs.some((t) => t.id === tabId)) return;

  useTabStore.setState((state) => ({
    tabs: state.tabs.map((t) =>
      t.id === tabId
        ? {
            ...t,
            name: editor.activePipelineName || t.name,
            pipeline: editor.pipeline,
            input: editor.input,
            output: editor.output,
            inputType: editor.inputType,
            inputTypeMode: editor.inputTypeMode,
            stepResults: exec.stepResults,
            pipelineVariables: editor.pipelineVariables,
            activePipelineName: editor.activePipelineName,
            activePipelineDescription: editor.activePipelineDescription,
            activePipelineId: editor.activePipelineId,
            activePipelineNameIsAuto: editor.activePipelineNameIsAuto,
            activePipelineDescriptionIsAuto: editor.activePipelineDescriptionIsAuto,
            inputPanelConfig: { ...viz.inputPanel },
            outputPanelConfig: { ...viz.outputPanel },
            inputActiveModes: viz.inputActiveModes,
            outputActiveModes: viz.outputActiveModes,
            isProcessing: false,
          }
        : t
    ),
  }));
}

/** Load a tab's state into appStore. */
export function loadTabStateToApp(tabId: string) {
  const tab = useTabStore.getState().tabs.find((t) => t.id === tabId);
  if (!tab) return;

  const reordered = reorderVisualizerStepsRuntime(tab.pipeline);
  const outputType = tab.stepResults.length > 0
    ? tab.stepResults[tab.stepResults.length - 1]?.outputType ?? null
    : null;

  useEditorStore.setState({
    pipeline: reordered,
    input: tab.input,
    output: tab.output,
    inputType: tab.inputType,
    outputType,
    inputTypeMode: tab.inputTypeMode,
    pipelineVariables: tab.pipelineVariables,
    activePipelineName: tab.activePipelineName,
    activePipelineDescription: tab.activePipelineDescription,
    activePipelineId: tab.activePipelineId,
    activePipelineNameIsAuto: tab.activePipelineNameIsAuto ?? true,
    activePipelineDescriptionIsAuto: tab.activePipelineDescriptionIsAuto ?? true,
  });

  useExecutionStore.setState((state) => ({
    stepResults: tab.stepResults,
    errors: {},
    error: null,
    isProcessing: false,
    stepThrough: null,
    debugSnapshot: null,
    isStepThrough: false,
    currentStepIndex: null,
    rerunFromStepIndex: null,
    suppressAutoRun: false,
    executionRevision: state.executionRevision + 1,
  }));

  const inputPanelConfig = tab.inputPanelConfig ?? {
    ...DEFAULT_PANEL_CONFIG,
    modifiers: { ...DEFAULT_PANEL_CONFIG.modifiers },
  };
  const outputPanelConfig = tab.outputPanelConfig ?? {
    ...DEFAULT_PANEL_CONFIG,
    modifiers: { ...DEFAULT_PANEL_CONFIG.modifiers },
  };

  useVisualizerStore.getState().loadPanelConfigs(
    inputPanelConfig,
    outputPanelConfig,
  );

  const inputActiveModes: VisualizerMode[] = tab.inputActiveModes?.length
    ? [...tab.inputActiveModes]
    : ['raw'];
  const outputActiveModes: VisualizerMode[] = tab.outputActiveModes?.length
    ? [...tab.outputActiveModes]
    : ['raw'];

  useVisualizerStore.setState({
    inputActiveModes,
    outputActiveModes,
  });
}

/** Hydrate app stores from the current active tab. */
export function hydrateActiveTabStateToApp() {
  loadTabStateToApp(useTabStore.getState().activeTabId);
}
